import { type ResponseError, type ErrorType, type ResponseOutput } from '../model'
export { ZodError } from 'zod'

const apiRequestErrors = new WeakSet<object>()

export const isResponseError = (r: unknown): r is ResponseError =>
  typeof r === 'object' && (r as ResponseOutput).type === 'error' && Boolean((r as ResponseError).error)

export const toResponseError = (error: unknown): ResponseError => {
  if (isResponseError(error)) return markApiRequestError(error)
  if (error instanceof APIError) return markApiRequestError(error.getResponseError())
  if (error instanceof TantalimError) return markApiRequestError(error.getResponseError())
  if (error instanceof Error) return { type: 'error', error: 'Server', message: error.message }
  if (typeof error === 'string') return { type: 'error', error: 'Server', message: error }
  // TODO this results in Sentry capturing an error "Object captured as exception with keys: error, message, type"
  return { type: 'error', error: 'Server', message: stringifyCircularJSON(error) }
}

/**
 * Identifies failures that originated from an API request and are expected to be
 * presented by the calling mutation hook. The WeakSet also preserves the plain
 * ResponseError shape used throughout the front model.
 */
export const isApiRequestError = (error: unknown): boolean => {
  if (error instanceof APIError || error instanceof TantalimError) return true
  return isResponseError(error) && apiRequestErrors.has(error)
}

function markApiRequestError<T extends ResponseError>(error: T): T {
  apiRequestErrors.add(error)
  return error
}

export function stringifyCircularJSON(obj: unknown): string {
  const seen = new WeakSet()
  return JSON.stringify(obj, (k, v) => {
    if (v !== null && typeof v === 'object') {
      if (seen.has(v)) return
      seen.add(v)
    }
    return v
  })
}

export class APIError extends Error {
  error: ResponseError

  constructor(error: ResponseError) {
    super(error.message ?? error.error)
    this.error = error
  }

  getResponseError(): ResponseError {
    return this.error
  }
}

export class TantalimError extends Error {
  error: ErrorType

  constructor(error: ErrorType, msg?: unknown) {
    super(messageFromError(msg || error))
    this.error = error
  }

  getResponseError(): ResponseError {
    const { error, message } = this
    return {
      type: 'error',
      error,
      message,
    }
  }
}

export function messageFromError(error: unknown): string | undefined {
  if (error === undefined) return
  if (error === null) return
  if (error instanceof TantalimError) return error.message || error.error
  if (error instanceof APIError) return error.message
  if (error instanceof Error) return error.message
  if (error && typeof error === 'object') {
    if ('message' in error && typeof error.message === 'string') return error.message
    if ('error' in error && typeof error.error === 'string') return error.error
  }
  if (typeof error === 'string') return error
  return 'unknown error type'
}
